// src/pages/Resources/ResourceDetailsPanel.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { acquireTokenForApi } from "@/authConfig";
import { ArchiveRunSummaryDto, getGroupArchiveRuns, startGroupArchive } from "@/api/sharepointApi";
import { getMyHighestRole } from "@/api/adminApi";
import {
  getResourceDetails,
  getResourceMembers,
  syncResourceMembers,
  addResourceMember,
  addGraphMember,
  removeResourceMember,
  removeGraphMember,
  updateMemberRole,
  updateGraphMemberRole,
  getResourceLists,
  getResourceListsByGraphId,
  getResourceSharingLinks,
  getListItems,
  getListItemsByGraphId,
  updateSiteSavedData,
  updateSiteSavedDataByGraphId,
  getGroupByGraphId,
  getGroupMembersByGraphId,
  getGroupReports,
  GroupGeneratedReport,
  GroupReportRun,
} from "@/services/resourcesApi";
import { ResourceDetails, ResourceMember, ResourceSummary, SharePointList, SharingLink } from "@/types";
import AddUserModal from "./AddUserModal";
import { GroupReportBar } from "@/components/GroupReportBar";
import ServiceIcons from "./ServiceIcons";
import SiteSavedDataModal from "./SiteSavedDataModal";
import DeleteGroupModal from "./DeleteGroupModal";
import SupportRequestModal from "./SupportRequestModal";
import SitePermissionsModal from "./SitePermissionsModal";
import CheckPermissionsModal from "./CheckPermissionsModal";
import UserAccessDetailsModal from "./UserAccessDetailsModal";
import ResourceFileInventory from "./ResourceFileInventory";
import { Dropdown, Button, Modal, Spinner } from "react-bootstrap";
import "./Resources.scss";

import {
  FaBox,
  FaComments,
  FaExternalLinkAlt,
  FaGlobe,
  FaShareSquare,
  FaUsers,
  FaTrash,
  FaUserPlus,
  FaFolder,
  FaFile,
  FaLock,
  FaChevronRight,
  FaLifeRing,
  FaPencilAlt,
  FaSync,
} from "react-icons/fa";

interface Props {
  internalResourceId: string | null;
  isGraphId?: boolean;  // If true, load directly from Graph API instead of database
  resourceSnapshot?: ResourceSummary;
  onMetadataSaved?: (
    target: { internalResourceId?: string | null; groupId?: string | null },
    updates: ResourceMetadataPatch
  ) => void;
}

interface ResourceMetadataPatch {
  primaryOwnerNtid?: string;
  secondaryOwnerNtid?: string;
  primaryAdminNtid?: string;
  secondaryAdminNtid?: string;
  businessUnit?: string;
  costCenter?: string;
  codeOfConductSensitive?: boolean;
  lastUpdated?: string;
}

type DetailsTab = "members" | "reports" | "lists" | "files" | "links" | "permissions" | "archive";

const ResourceDetailsPanel: React.FC<Props> = ({ internalResourceId, isGraphId = false, resourceSnapshot, onMetadataSaved }) => {
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username?.toLowerCase();
  
  const [details, setDetails] = useState<ResourceDetails | null>(null);
  const [members, setMembers] = useState<ResourceMember[]>([]);
  const [lists, setLists] = useState<SharePointList[]>([]);
  const [sharingLinks, setSharingLinks] = useState<SharingLink[]>([]);
  const [selectedList, setSelectedList] = useState<SharePointList | null>(null);
  const [listItems, setListItems] = useState<any[]>([]);
  const [loadingListItems, setLoadingListItems] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [archiveRuns, setArchiveRuns] = useState<ArchiveRunSummaryDto[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveActionLoading, setArchiveActionLoading] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [refreshingMembers, setRefreshingMembers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailsTab>("members");
  const [groupReportRuns, setGroupReportRuns] = useState<GroupReportRun[]>([]);
  const [groupGeneratedReports, setGroupGeneratedReports] = useState<GroupGeneratedReport[]>([]);
  const [loadingGroupReports, setLoadingGroupReports] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [permissionsModalItem, setPermissionsModalItem] = useState<any>(null);
  const [showSiteDataModal, setShowSiteDataModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showSitePermissionsModal, setShowSitePermissionsModal] = useState(false);
  const [showCheckPermissionsModal, setShowCheckPermissionsModal] = useState(false);
  const [selectedUserForDetails, setSelectedUserForDetails] = useState<{id: string, name: string} | null>(null);
  const [metaModalRequested, setMetaModalRequested] = useState(false);
  const [hasHandledAutoMetadataModal, setHasHandledAutoMetadataModal] = useState(false);
  const [successModal, setSuccessModal] = useState({ show: false, title: '', message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const [confirmModal, setConfirmModal] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: async () => {} 
  });


  const handleOpenSite = () => {
    const siteUrl = details?.webUrl || details?.sharepointUrl || details?.url;
    if (siteUrl) window.open(siteUrl, "_blank");
  };

  const handleManageOwners = () => {
    setShowAddUserModal(true);
  };

  const handleReviewSharing = () => {
    // Navigate to sharing tab
    setTab("links");
  };

  const handleEditMetadata = () => {
    setShowSiteDataModal(true);
  };

  const parseMetadata = (metadataJson: string | null | undefined): ResourceMetadataPatch => {
    try {
      if (!metadataJson) return {};
      return JSON.parse(metadataJson) as ResourceMetadataPatch;
    } catch {
      return {};
    }
  };

  const patchDetailsMetadata = (current: ResourceDetails, updates: ResourceMetadataPatch): ResourceDetails => {
    const existingMetadata = parseMetadata(current.metadataJson);
    const mergedMetadata = { ...existingMetadata, ...updates };
    const reviewedAt = new Date();
    const nextReview = new Date(reviewedAt.getTime() + 365 * 24 * 60 * 60 * 1000);

    return {
      ...current,
      metadataJson: JSON.stringify(mergedMetadata),
      primaryOwner: mergedMetadata.primaryOwnerNtid || current.primaryOwner,
      secondaryOwner: mergedMetadata.secondaryOwnerNtid || current.secondaryOwner,
      metadataLastReviewedBy: "current-user",
      metadataLastReviewedDt: reviewedAt.toISOString(),
      metadataNextReviewDt: nextReview.toISOString(),
    };
  };

  const mergeSnapshotDetails = (current: ResourceDetails, snapshot: ResourceSummary): ResourceDetails => ({
    ...current,
    metadataJson: snapshot.metadataJson ?? current.metadataJson,
    primaryOwner: snapshot.primaryOwner ?? current.primaryOwner,
    secondaryOwner: snapshot.secondaryOwner ?? current.secondaryOwner,
    metadataLastReviewedBy: snapshot.metadataLastReviewedBy ?? current.metadataLastReviewedBy,
    metadataLastReviewedDt: snapshot.metadataLastReviewedDt ?? current.metadataLastReviewedDt,
    metadataNextReviewDt: snapshot.metadataNextReviewDt ?? current.metadataNextReviewDt,
  });

  const handleSaveSiteData = async (data: any) => {
    if (!internalResourceId) return;

    if (isGraphId) {
      await updateSiteSavedDataByGraphId(internalResourceId, data);
    } else {
      await updateSiteSavedData(internalResourceId, data);
    }

    setDetails((current) => current ? patchDetailsMetadata(current, data) : current);
    onMetadataSaved?.(
      {
        internalResourceId: details?.internalResourceId || (!isGraphId ? internalResourceId : null),
        groupId: details?.groupId || (isGraphId ? internalResourceId : null),
      },
      data
    );
  };

  const handleDeleteGroup = async () => {
    if (!internalResourceId) return;
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/resources/${internalResourceId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${await acquireTokenForApi()}`,
          },
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to delete group');
      }
      
      setSuccessModal({
        show: true,
        title: 'Group Deleted',
        message: `The group "${details?.displayName}" has been permanently deleted.`,
      });
      
      // Close the details panel after a delay
      setTimeout(() => {
        window.location.href = '/resources'; // Navigate back to resources list
      }, 2000);
    } catch (error: any) {
      console.error('Failed to delete group:', error);
      setErrorModal({
        show: true,
        message: error.message || 'Failed to delete group. Please try again.',
      });
      throw error;
    }
  };

  const handleSupportRequest = async (message: string) => {
    if (!internalResourceId) return;
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/support/group-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await acquireTokenForApi()}`,
          },
          body: JSON.stringify({
            groupId: internalResourceId,
            groupName: details?.displayName,
            groupUrl: details?.url || details?.siteUrl || details?.webUrl,
            message: message.trim(),
          }),
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to submit support request');
      }
      
      setSuccessModal({
        show: true,
        title: 'Support Request Submitted',
        message: 'Your request has been sent to the Service Desk. You will receive a confirmation email shortly.',
      });
    } catch (error: any) {
      console.error('Failed to submit support request:', error);
      setErrorModal({
        show: true,
        message: error.message || 'Failed to submit support request. Please try again.',
      });
      throw error;
    }
  };

  const handleAddUser = async (userId: string, role: "Owner" | "Member") => {
    if (!internalResourceId) return;
    
    try {
      if (isGraphId) {
        await addGraphMember(internalResourceId, userId, role);
      } else {
        await addResourceMember(internalResourceId, userId, role);
      }
      // Reload members
      const updated = isGraphId
        ? await getGroupMembersByGraphId(internalResourceId)
        : await getResourceMembers(internalResourceId);
      setMembers(updated);
      setSuccessModal({
        show: true,
        title: 'Member Added',
        message: 'Successfully added member to the group.'
      });
    } catch (error) {
      console.error("Failed to add member:", error);
      setErrorModal({
        show: true,
        message: 'Failed to add member. Please try again.'
      });
      throw error; // Re-throw so AddUserModal knows it failed
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!internalResourceId) return;
    
    setConfirmModal({
      show: true,
      title: 'Remove Member',
      message: 'Are you sure you want to remove this user from the group?',
      onConfirm: async () => {
        try {
          if (isGraphId) {
            await removeGraphMember(internalResourceId, userId);
          } else {
            await removeResourceMember(internalResourceId, userId);
          }
          // Reload members
          const updated = isGraphId
            ? await getGroupMembersByGraphId(internalResourceId)
            : await getResourceMembers(internalResourceId);
          setMembers(updated);
          setSuccessModal({
            show: true,
            title: 'Member Removed',
            message: 'Successfully removed member from the group.'
          });
        } catch (error) {
          console.error("Failed to remove member:", error);
          setErrorModal({
            show: true,
            message: 'Failed to remove member. Please try again.'
          });
        }
        setConfirmModal({ show: false, title: '', message: '', onConfirm: async () => {} });
      }
    });
  };

  const handleChangeRole = async (userId: string, newRole: "Owner" | "Member") => {
    if (!internalResourceId) return;
    
    try {
      if (isGraphId) {
        await updateGraphMemberRole(internalResourceId, userId, newRole);
      } else {
        await updateMemberRole(internalResourceId, userId, newRole);
      }
      // Reload members
      const updated = isGraphId
        ? await getGroupMembersByGraphId(internalResourceId)
        : await getResourceMembers(internalResourceId);
      setMembers(updated);
    } catch (error) {
      console.error("Failed to change role:", error);
      alert("Failed to change role. Please try again.");
    }
  };

  const handleRefreshMembers = async () => {
    if (!internalResourceId) return;
    
    setRefreshingMembers(true);
    try {
      if (isGraphId) {
        const refreshedMembers = await getGroupMembersByGraphId(internalResourceId);
        setMembers(refreshedMembers);
        setSuccessModal({
          show: true,
          title: 'Members Refreshed',
          message: `Successfully refreshed ${refreshedMembers.length} members from Microsoft 365.`
        });
        return;
      }

      // Sync members from Microsoft Graph API
      const result = await syncResourceMembers(internalResourceId);
      setMembers(result.members);
      setSuccessModal({
        show: true,
        title: 'Members Refreshed',
        message: `Successfully synced ${result.syncedCount} members from Microsoft 365.`
      });
    } catch (error) {
      console.error("Failed to refresh members:", error);
      setErrorModal({
        show: true,
        message: 'Failed to refresh members. Please try again.'
      });
    } finally {
      setRefreshingMembers(false);
    }
  };

  const handleListClick = async (list: SharePointList) => {
    if (!internalResourceId) return;
    
    console.log('[handleListClick] Fetching items for list:', list.id, list.displayName);
    setSelectedList(list);
    setLoadingListItems(true);
    
    try {
      const items = isGraphId
        ? await getListItemsByGraphId(internalResourceId, list.id)
        : await getListItems(internalResourceId, list.id);
      console.log('[handleListClick] Received items:', items?.length, items);
      if (items?.length > 0) {
        console.log('[handleListClick] First item structure:', items[0]);
        console.log('[handleListClick] First item fields:', items[0]?.fields);
      }
      setListItems(items);
    } catch (error) {
      console.error("Failed to load list items:", error);
      setListItems([]);
    } finally {
      setLoadingListItems(false);
    }
  };

  const handleCheckPermissions = (item: any) => {
    setPermissionsModalItem(item);
  };

  const handleClosePermissionsModal = () => {
    setPermissionsModalItem(null);
  };

  const handleCloseListItems = () => {
    setSelectedList(null);
    setListItems([]);
  };

  useEffect(() => {
    const loadRole = async () => {
      try {
        const roleData = await getMyHighestRole();
        const role = String(roleData?.role || "").trim().toLowerCase();
        setIsSuperUser(role === "superadmin" || role === "superuser" || role === "super user");
      } catch {
        setIsSuperUser(false);
      }
    };

    loadRole();
  }, []);

  useEffect(() => {
    if (!internalResourceId) {
      setDetails(null);
      setMembers([]);
      setLists([]);
      setSharingLinks([]);
      setSelectedList(null);
      setListItems([]);
      setError(null);
      setHasHandledAutoMetadataModal(false);
      return;
    }

    setHasHandledAutoMetadataModal(false);

    const load = async () => {
      setLoadingDetails(true);
      setLoadingMembers(true);
      setError(null);
      setDetails(null);
      setMembers([]);
      setLists([]);
      setSharingLinks([]);
      setSelectedList(null);
      setListItems([]);

      try {
        const [d, m] = await Promise.all([
          isGraphId 
            ? getGroupByGraphId(internalResourceId)
            : getResourceDetails(internalResourceId),
          isGraphId 
            ? getGroupMembersByGraphId(internalResourceId)
            : getResourceMembers(internalResourceId),
        ]);

        setDetails(d);
        setMembers(m);
      } catch (err: any) {
        console.error("Failed to load resource details", err);
        setError(err.message ?? "Failed to load resource details");
        setDetails(null);
        setMembers([]);
      } finally {
        setLoadingDetails(false);
        setLoadingMembers(false);
      }
    };

    load();
  }, [internalResourceId, isGraphId]);

  useEffect(() => {
    if (!resourceSnapshot) return;

    setDetails((current) => current ? mergeSnapshotDetails(current, resourceSnapshot) : current);
  }, [
    resourceSnapshot?.metadataJson,
    resourceSnapshot?.metadataLastReviewedBy,
    resourceSnapshot?.metadataLastReviewedDt,
    resourceSnapshot?.metadataNextReviewDt,
    resourceSnapshot?.primaryOwner,
    resourceSnapshot?.secondaryOwner,
  ]);

  // Load lists when tab changes to "lists"
  useEffect(() => {
    if (tab === "lists" && internalResourceId && lists.length === 0) {
      const loadLists = async () => {
        setLoadingLists(true);
        try {
          const data = isGraphId
            ? await getResourceListsByGraphId(internalResourceId)
            : await getResourceLists(internalResourceId);
          setLists(data);
        } catch (err) {
          console.error("Failed to load lists", err);
        } finally {
          setLoadingLists(false);
        }
      };
      loadLists();
    }
  }, [tab, internalResourceId, lists.length, isGraphId]);

  // Load sharing links when tab changes to "links"
  useEffect(() => {
    if (tab === "links" && internalResourceId) {
      const loadLinks = async () => {
        console.log('[Sharing Links] Loading sharing links for:', internalResourceId);
        setLoadingLinks(true);
        try {
          const data = await getResourceSharingLinks(internalResourceId);
          console.log('[Sharing Links] Received data:', data?.length, data);
          setSharingLinks(data || []);
        } catch (err) {
          console.error("Failed to load sharing links", err);
          setSharingLinks([]);
        } finally {
          setLoadingLinks(false);
          console.log('[Sharing Links] Loading complete');
        }
      };
      loadLinks();
    }
  }, [tab, internalResourceId]);

  useEffect(() => {
    if (!isSuperUser && (tab === "reports" || tab === "links" || tab === "permissions" || tab === "archive")) {
      setTab("members");
    }
  }, [isSuperUser, tab]);

  useEffect(() => {
    const groupGuid = details?.groupId || (isGraphId ? internalResourceId : undefined);
    // Don't load reports if user is not superadmin or tab is not reports
    if (!isSuperUser || tab !== "reports" || !groupGuid) return;

    const loadGroupReports = async () => {
      setLoadingGroupReports(true);
      try {
        const result = await getGroupReports(groupGuid);
        setGroupReportRuns(result.runs || []);
        setGroupGeneratedReports(result.generated || []);
      } catch (err) {
        console.error("Failed to load group reports", err);
        setGroupReportRuns([]);
        setGroupGeneratedReports([]);
      } finally {
        setLoadingGroupReports(false);
      }
    };

    loadGroupReports();
  }, [tab, details?.groupId, isGraphId, internalResourceId, isSuperUser]);

  useEffect(() => {
    const groupGuid = details?.groupId || (isGraphId ? internalResourceId : undefined);
    if (tab !== "archive" || !groupGuid) return;

    const loadArchiveRuns = async () => {
      setArchiveLoading(true);
      try {
        const runs = await getGroupArchiveRuns(groupGuid);
        setArchiveRuns(runs);
      } catch (err) {
        console.error("Failed to load archive runs", err);
        setArchiveRuns([]);
      } finally {
        setArchiveLoading(false);
      }
    };

    loadArchiveRuns();
  }, [tab, details?.groupId, isGraphId, internalResourceId]);

  // Auto-open metadata modal if ?meta param present in URL
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setMetaModalRequested(params.has("meta"));
  }, [location.search]);

  useEffect(() => {
    if (loadingDetails || !details || showSiteDataModal || hasHandledAutoMetadataModal) return;

    const reviewMetadataMissing = !details.metadataLastReviewedDt;
    if (metaModalRequested || reviewMetadataMissing) {
      setShowSiteDataModal(true);
    }

    setHasHandledAutoMetadataModal(true);
  }, [
    metaModalRequested,
    loadingDetails,
    details,
    showSiteDataModal,
    hasHandledAutoMetadataModal,
  ]);

  const handleStartArchive = async () => {
    const groupGuid = details?.groupId || (isGraphId ? internalResourceId : undefined);
    if (!groupGuid) return;

    try {
      setArchiveActionLoading(true);
      const latest = archiveRuns[0]?.guid;
      await startGroupArchive(groupGuid, latest);
      const refreshed = await getGroupArchiveRuns(groupGuid);
      setArchiveRuns(refreshed);
      setSuccessModal({
        show: true,
        title: "Archive Queued",
        message: "Backup stage has been queued for this group."
      });
    } catch (error: any) {
      setErrorModal({
        show: true,
        message: error?.response?.data?.error || error?.message || "Failed to queue archive backup."
      });
    } finally {
      setArchiveActionLoading(false);
    }
  };

  const owners = useMemo(
    () =>
      members
        .filter((m) => (m.role ?? "").toLowerCase().includes("owner"))
        .sort((a, b) =>
          (a.displayName ?? "").localeCompare(b.displayName ?? "")
        ),
    [members]
  );

  const others = useMemo(
    () =>
      members
        .filter((m) => !(m.role ?? "").toLowerCase().includes("owner"))
        .sort((a, b) =>
          (a.displayName ?? "").localeCompare(b.displayName ?? "")
        ),
    [members]
  );

  // Check if current user is an owner of this resource
  const currentUserIsOwner = useMemo(() => {
    if (!currentUserEmail) return false;
    return members.some(
      (m) => 
        (m.email?.toLowerCase() === currentUserEmail || m.upn?.toLowerCase() === currentUserEmail) &&
        (m.role ?? "").toLowerCase().includes("owner")
    );
  }, [members, currentUserEmail]);

  const fileInventoryGroupId = details?.groupId || (isGraphId ? internalResourceId : null);
  const canViewFileInventory =
    Boolean(fileInventoryGroupId) && (currentUserIsOwner || resourceSnapshot?.isOwner === true || isSuperUser);
  const [fileInventoryOpened, setFileInventoryOpened] = useState(false);

  useEffect(() => {
    setFileInventoryOpened(false);
  }, [fileInventoryGroupId]);

  useEffect(() => {
    if (tab === "files") setFileInventoryOpened(true);
  }, [tab]);

  useEffect(() => {
    if (tab === "files" && !canViewFileInventory && !loadingMembers && !loadingDetails) {
      setTab("members");
    }
  }, [tab, canViewFileInventory, loadingMembers, loadingDetails]);

  const formatDate = (value?: string | Date | null) => {
    if (!value) return "-";
    const d = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString();
  };

  const metadataInitialData = useMemo(() => {
    let parsed: any = {};
    if (details?.metadataJson) {
      try {
        parsed = JSON.parse(details.metadataJson);
      } catch {
        parsed = {};
      }
    }

    return {
      ...parsed,
      primaryOwnerNtid: parsed?.primaryOwnerNtid || details?.primaryOwner || "",
      secondaryOwnerNtid: parsed?.secondaryOwnerNtid || details?.secondaryOwner || "",
      primaryAdminNtid: parsed?.primaryAdminNtid || "",
      secondaryAdminNtid: parsed?.secondaryAdminNtid || "",
      businessUnit: parsed?.businessUnit || "",
      costCenter: parsed?.costCenter || "",
      codeOfConductSensitive: Boolean(parsed?.codeOfConductSensitive)
    };
  }, [details?.metadataJson, details?.primaryOwner, details?.secondaryOwner]);

  const metadataDisplay = useMemo(() => {
    const primaryOwner = metadataInitialData.primaryOwnerNtid || details?.primaryOwner || "—";
    const secondaryOwner = metadataInitialData.secondaryOwnerNtid || details?.secondaryOwner || "—";

    return {
      primaryOwner,
      secondaryOwner,
      businessUnit: metadataInitialData.businessUnit || "—",
      costCenter: metadataInitialData.costCenter || "—",
      codeOfConductSensitive: Boolean(metadataInitialData.codeOfConductSensitive),
    };
  }, [
    details?.primaryOwner,
    details?.secondaryOwner,
    metadataInitialData.businessUnit,
    metadataInitialData.codeOfConductSensitive,
    metadataInitialData.costCenter,
    metadataInitialData.primaryOwnerNtid,
    metadataInitialData.secondaryOwnerNtid,
  ]);

  const metadataReviewInfo = useMemo(() => {
    if (!details?.metadataLastReviewedDt) return null;

    const reviewedAt = formatDate(details.metadataLastReviewedDt);
    const reviewedBy = details.metadataLastReviewedBy?.trim();
    const nextReview = details.metadataNextReviewDt ? formatDate(details.metadataNextReviewDt) : null;

    const reviewedText = reviewedBy
      ? `Last reviewed by ${reviewedBy} on ${reviewedAt}`
      : `Last reviewed on ${reviewedAt}`;

    return nextReview ? `${reviewedText}. Next review due ${nextReview}.` : `${reviewedText}.`;
  }, [details?.metadataLastReviewedBy, details?.metadataLastReviewedDt, details?.metadataNextReviewDt]);

  const renderMemberRow = (m: ResourceMember) => {
    const isPrimary = m.isPrimaryOwner;
    const isSecondary = m.isSecondaryOwner;
    const isGuest = m.isGuest || m.upn?.includes('#EXT#');
    const isCurrentUser = currentUserEmail && 
      (m.email?.toLowerCase() === currentUserEmail || m.upn?.toLowerCase() === currentUserEmail);
    const badges: React.ReactNode[] = [];

    if (isPrimary) {
      badges.push(
        <span key="primary" className="member-role-icon primary" title="Primary owner">
          👑
        </span>
      );
    }
    if (isSecondary) {
      badges.push(
        <span
          key="secondary"
          className="member-role-icon secondary"
          title="Secondary owner"
        >
          ⭐
        </span>
      );
    }
    if (m.isSiteAdmin) {
      badges.push(
        <span
          key="siteadmin"
          className="member-role-icon site-admin"
          title="Site admin"
        >
          ⚙
        </span>
      );
    }

    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();
    };

    // Determine if user can modify this member
    // Owners can modify anyone, Members can only remove themselves
    const canChangeRole = currentUserIsOwner && !isGuest;
    const canRemove = currentUserIsOwner || isCurrentUser;

    return (
      <tr key={m.id ?? `${m.upn ?? m.email ?? m.displayName}`}>
        <td>
          <div className="d-flex align-items-center gap-2">
            <div className="member-avatar">
              {m.photoUrl ? (
                <img
                  src={m.photoUrl}
                  alt={m.displayName}
                  className="member-avatar-img"
                />
              ) : (
                <div className="member-avatar-initials">
                  {getInitials(m.displayName ?? m.upn ?? '?')}
                </div>
              )}
            </div>
            <div className="d-flex flex-column">
              <div>
                <span className="fw-semibold">{m.displayName ?? m.upn}</span>
                {" "}
                <span className="text-muted">{m.email ?? m.upn ?? "-"}</span>
              </div>
              {m.jobTitle && (
                <span className="text-muted" style={{ fontSize: '12px' }}>
                  {m.jobTitle}
                </span>
              )}
              {m.lastCheckedDt && (
                <span className="text-muted" style={{ fontSize: '11px' }} title={new Date(m.lastCheckedDt).toLocaleString()}>
                  Last checked: {formatDate(m.lastCheckedDt)}
                </span>
              )}
            </div>
          </div>
        </td>
        <td className="align-middle">
          {isGuest ? (
            <span className="badge bg-secondary">Guest</span>
          ) : canChangeRole ? (
            <Dropdown>
              <Dropdown.Toggle variant="light" size="sm" className="border">
                {m.role ?? "Member"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => handleChangeRole(m.upn, "Member")}
                  disabled={m.role === "Member"}
                >
                  Change to Member
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => handleChangeRole(m.upn, "Owner")}
                  disabled={m.role === "Owner"}
                >
                  Change to Owner
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <span className="badge bg-light text-dark border">
              {m.role ?? "Member"}
            </span>
          )}
        </td>
        <td className="align-middle text-center">{badges}</td>
        <td className="align-middle text-end">
          {canRemove ? (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleRemoveMember(m.upn)}
              title={isCurrentUser ? "Leave group" : "Remove user"}
            >
              <FaTrash />
            </Button>
          ) : (
            <span className="text-muted small">-</span>
          )}
        </td>
      </tr>
    );
  };

  // Empty / placeholder state
  if (!internalResourceId) {
    return (
      <div className="resource-details-panel empty-state">
        <div className="text-muted">Select a resource to see details.</div>
      </div>
    );
  }

  if (loadingDetails && !details) {
    return (
      <div className="resource-details-panel resource-details-loading-state">
        <div className="resource-details-loading-content text-muted">
          <Spinner animation="border" />
          Loading group details...
        </div>
      </div>
    );
  }

  return (
    <div className="resource-details-panel">
      {/* Loading / error overlay */}
      {loadingDetails && (
        <div className="resource-details-loading text-muted small">
          Loading details…
        </div>
      )}
      {error && !loadingDetails && (
        <div className="alert alert-danger py-2 mb-2 small">{error}</div>
      )}

      <div className="resource-details-content-fade-in">
      {/* Header card */}
      <div className="resource-header-card">
        <div className="d-flex">
          <div className="resource-header-main flex-grow-1">
            <div className="d-flex align-items-start gap-2">
              <div className="resource-thumbnail large">
                <span className="resource-thumbnail-icon">
                  {{
                    group: <FaUsers />,
                    site: <FaGlobe />,
                    team: <FaComments />,
                  }[details?.resourceType?.toLowerCase() ?? ""] || <FaBox />}
                </span>
              </div>
              <div className="d-flex flex-column">
                <h5 className="mb-1">
                  {details?.displayName ?? "(no name)"}
                </h5>
                <div className="resource-type-row text-muted small">
                  <span>{details?.resourceType ?? "Unknown type"}</span>
                  {currentUserIsOwner && (
                    <span className="resource-report-bar-inline">
                      <GroupReportBar
                        context={{
                          siteUrl: details?.siteUrl || details?.sharepointUrl || details?.webUrl || details?.url,
                          groupId: details?.groupId ?? undefined,
                          displayName: details?.displayName,
                        }}
                        compact
                      />
                    </span>
                  )}
                </div>
                {details?.url && (
                  <div className="mt-1 small">
                    <a
                      href={details.url}
                      target="_blank"
                      rel="noreferrer"
                      className="resource-link"
                    >
                      <span className="resource-link-text">{details.url}</span>
                      <span className="resource-link-icon" title="Open">
                        ↗
                      </span>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: actions/menu */}
          <div className="resource-actions-row d-flex align-items-center gap-3">
            {/* Service Icons */}
            <ServiceIcons
              team={details?.team}
              plannerPlans={details?.plannerPlans}
              siteUrl={details?.webUrl || details?.sharepointUrl || details?.url}
            />

            <div className="d-flex gap-2">
              <button className="resource-action-btn" onClick={handleOpenSite} title="Open site">
                <FaGlobe />
              </button>

              <button className="resource-action-btn" onClick={handleManageOwners} title="Add users">
                <FaUserPlus />
              </button>

              {isSuperUser && (
                <button className="resource-action-btn" onClick={handleReviewSharing} title="Review sharing">
                  <FaShareSquare />
                </button>
              )}

              <button 
                className="resource-action-btn" 
                onClick={() => setShowSupportModal(true)} 
                title="Request support"
              >
                <FaLifeRing />
              </button>

              {currentUserIsOwner && (
                <button 
                  className="resource-action-btn text-danger" 
                  onClick={() => setShowDeleteModal(true)} 
                  title="Delete group"
                >
                  <FaTrash />
                </button>
              )}
            </div>
          </div>

        </div>

        <div className="resource-meta-row">
          <div className="resource-meta-card">
            <div className="resource-meta-line owners-line">
              <div className="meta-item">
                <span className="meta-label">Primary owner</span>
                <span className="meta-value">
                  {metadataDisplay.primaryOwner}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Secondary owner</span>
                <span className="meta-value">
                  {metadataDisplay.secondaryOwner}
                </span>
              </div>
              <div className="meta-item">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleEditMetadata}
                  title="Edit metadata"
                  aria-label="Edit metadata"
                >
                  <FaPencilAlt />
                </Button>
              </div>
            </div>

            <div className="resource-meta-line business-line">
              <div className="meta-item">
                <span className="meta-label">Business unit</span>
                <span className="meta-value">{metadataDisplay.businessUnit}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Cost center</span>
                <span className="meta-value">{metadataDisplay.costCenter}</span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Code of conduct sensitive</span>
                <span className="meta-value">{metadataDisplay.codeOfConductSensitive ? "Yes" : "No"}</span>
              </div>
            </div>

            <div className="resource-meta-line details-line">
              <div className="meta-item">
                <span className="meta-label">Sensitivity</span>
                <span className="meta-value">
                  {details?.sensitivityLabel ?? "—"}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Retention</span>
                <span className="meta-value">
                  {details?.retentionLabel ?? "—"}
                </span>
              </div>
              <div className="meta-item">
                <span className="meta-label">Last crawl</span>
                <span className="meta-value">
                  {details?.lastCrawlDt
                    ? formatDate(details.lastCrawlDt)
                    : "—"}
                </span>
              </div>
            </div>

            {metadataReviewInfo && (
              <div className="resource-meta-review-note" aria-label="Metadata review status">
                <em>{metadataReviewInfo}</em>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Inner tabs (Members / Lists / Links / Permissions) */}
      <div className="resource-inner-tabs d-flex gap-2 mt-3 mb-2">
        <div
          className={
            "menu-item purple small" + (tab === "members" ? " selected" : "")
          }
          onClick={() => setTab("members")}
        >
          Members
        </div>
        {isSuperUser && (
          <div
            className={
              "menu-item purple small" +
              (tab === "reports" ? " selected" : "")
            }
            onClick={() => setTab("reports")}
          >
            Reports
          </div>
        )}
        <div
          className={
            "menu-item purple small" +
            (tab === "lists" ? " selected" : "")
          }
          onClick={() => setTab("lists")}
        >
          Lists & libraries
        </div>
        {canViewFileInventory && (
          <div
            className={
              "menu-item purple small" +
              (tab === "files" ? " selected" : "")
            }
            onClick={() => setTab("files")}
          >
            File inventory
          </div>
        )}
        {isSuperUser && (
          <>
            <div
              className={
                "menu-item purple small" +
                (tab === "links" ? " selected" : "")
              }
              onClick={() => setTab("links")}
            >
              Sharing links
            </div>
            <div
              className={
                "menu-item purple small" +
                (tab === "permissions" ? " selected" : "")
              }
              onClick={() => setTab("permissions")}
            >
              Permissions
            </div>
            <div
              className={
                "menu-item purple small" +
                (tab === "archive" ? " selected" : "")
              }
              onClick={() => setTab("archive")}
            >
              Archive
            </div>
          </>
        )}
      </div>
      </div>

      {/* Tab content */}
      <div className="resource-tab-content">
        {tab === "members" && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                {loadingMembers && (
                  <span className="text-muted small">
                    Loading members…
                  </span>
                )}
                {!loadingMembers && members.length === 0 && (
                  <span className="text-muted small">
                    No membership data found.
                  </span>
                )}
              </div>
              {!loadingMembers && members.length > 0 && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRefreshMembers}
                  disabled={refreshingMembers}
                  title="Sync members from Microsoft 365"
                >
                  {refreshingMembers ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <FaSync className="me-1" />
                      Refresh Members
                    </>
                  )}
                </Button>
              )}
            </div>

            {!loadingMembers && members.length > 0 && (
              <>
                {/* Owners */}
                {owners.length > 0 && (
                  <>
                    <div className="section-header small text-uppercase mb-1">
                      Owners
                    </div>
                    <table className="table table-sm resource-members-table">
                      <tbody>{owners.map(renderMemberRow)}</tbody>
                    </table>
                  </>
                )}

                {/* Members */}
                {others.length > 0 && (
                  <>
                    <div className="section-header small text-uppercase mt-3 mb-1">
                      Members
                    </div>
                    <table className="table table-sm resource-members-table">
                      <tbody>{others.map(renderMemberRow)}</tbody>
                    </table>
                  </>
                )}
              </>
            )}
          </>
        )}

        {tab === "reports" && (
          <>
            {currentUserIsOwner && (
              <div className="resource-reports-panel mb-3">
                <div className="alert alert-light border small mb-3">
                  Run configured group reports from here.
                </div>
                <GroupReportBar
                  context={{
                    siteUrl: details?.siteUrl || details?.sharepointUrl || details?.webUrl || details?.url,
                    groupId: details?.groupId ?? undefined,
                    displayName: details?.displayName,
                  }}
                  compact={false}
                />
              </div>
            )}

            <div className="resource-reports-panel">
              <h6 className="mb-3">Generated Reports</h6>

              {loadingGroupReports && <div className="text-muted small">Loading reports...</div>}

              {!loadingGroupReports && groupReportRuns.length === 0 && (
                <div className="text-muted small">No report runs found for this group.</div>
              )}

              {!loadingGroupReports && groupReportRuns.length > 0 && (
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Run</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Generated Files</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupReportRuns.map((run) => (
                        <tr key={run.m365ReportRunId}>
                          <td>{run.reportTitle}</td>
                          <td>
                            <span className={`badge ${
                              run.reportStatus === "Complete"
                                ? "bg-success"
                                : run.reportStatus === "Failed"
                                ? "bg-danger"
                                : "bg-info"
                            }`}>
                              {run.reportStatus}
                            </span>
                          </td>
                          <td>{formatDate(run.createDt)}</td>
                          <td>{run.generatedReportsCount ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!loadingGroupReports && groupGeneratedReports.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>File</th>
                        <th>Report</th>
                        <th>Generated</th>
                        <th>Access</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupGeneratedReports.map((report) => (
                        <tr key={report.m365AdminReportId}>
                          <td>{report.reportFilename}</td>
                          <td>{report.reportTitle}</td>
                          <td>{formatDate(report.createDt)}</td>
                          <td>
                            <span className="text-muted small">Request access from data owners</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Lists & Libraries Tab */}
        {tab === "lists" && (
          <div className="row">
            {/* Lists table */}
            <div className={selectedList ? "col-md-6" : "col-md-12"}>
              {loadingLists && (
                <div className="text-muted small mb-1">
                  Loading lists…
                </div>
              )}
              {!loadingLists && lists.length === 0 && (
                <div className="text-muted small">
                  No lists or libraries found.
                </div>
              )}
              {!loadingLists && lists.length > 0 && (
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th className="text-end">Items</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lists.map((list) => (
                      <tr
                        key={list.id}
                        className={selectedList?.id === list.id ? "table-active" : ""}
                      >
                        <td
                          className="fw-semibold cursor-pointer text-primary"
                          onClick={() => handleListClick(list)}
                          style={{ cursor: "pointer" }}
                        >
                          {list.displayName || list.name}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border">
                            {list.listType}
                          </span>
                        </td>
                        <td className="text-end">{list.itemCount ?? "—"}</td>
                        <td className="text-end">
                          <Button
                            as="a"
                            href={list.webUrl}
                            target="_blank"
                            rel="noreferrer"
                            variant="outline-primary"
                            size="sm"
                          >
                            <FaExternalLinkAlt /> Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* List items panel */}
            {selectedList && (
              <div className="col-md-6 border-start">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">{selectedList.displayName || selectedList.name}</h6>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCloseListItems}
                  >
                    Close
                  </Button>
                </div>

                {loadingListItems && (
                  <div className="text-muted small">Loading items…</div>
                )}

                {!loadingListItems && listItems.length === 0 && (
                  <div className="text-muted small">No items found.</div>
                )}

                {!loadingListItems && listItems.length > 0 && (
                  <div className="list-items" style={{ maxHeight: "400px", overflowY: "auto" }}>
                    {listItems.map((item: any, index: number) => {
                      // Fields are in item.fields.additionalData for Graph SDK objects
                      const fields = item.fields?.additionalData || item.fields || {};
                      const isFolder = item.folder != null || fields.FSObjType === "1" || fields.ContentType === "Folder";
                      
                      // Try multiple field name variations
                      const itemName = fields.FileLeafRef || fields.Title || fields.Name || item.name || "Untitled";
                      const modified = fields.Modified || item.lastModifiedDateTime;
                      const modifiedBy = fields.Editor || fields.Author;
                      
                      return (
                        <div
                          key={item.id || index}
                          className="list-item p-2 mb-2 border rounded"
                        >
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex align-items-center flex-grow-1">
                              {isFolder ? (
                                <FaFolder className="text-warning me-2" size={18} />
                              ) : (
                                <FaFile className="text-primary me-2" size={16} />
                              )}
                              <div className="flex-grow-1">
                                <div className="fw-semibold">
                                  {isFolder && (
                                    <FaChevronRight className="me-1" size={12} />
                                  )}
                                  {itemName}
                                </div>
                                <div className="text-muted small">
                                  {modified && (
                                    <span>Modified: {new Date(modified).toLocaleDateString()}</span>
                                  )}
                                  {modifiedBy && (
                                    <span className="ms-2">by {modifiedBy}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                title="Check Permissions"
                                onClick={() => handleCheckPermissions(item)}
                              >
                                <FaLock />
                              </Button>
                              {item.webUrl && (
                                <Button
                                  as="a"
                                  href={item.webUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  variant="outline-primary"
                                  size="sm"
                                  title="Open in SharePoint"
                                >
                                  <FaExternalLinkAlt />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Sharing Links Tab */}
        {isSuperUser && tab === "links" && (
          <>
            {loadingLinks && (
              <div className="text-muted small mb-1">
                Loading sharing links…
              </div>
            )}
            {!loadingLinks && sharingLinks.length === 0 && (
              <div className="text-muted small">
                No sharing links found.
              </div>
            )}
            {!loadingLinks && sharingLinks.length > 0 && (
              <table className="table table-sm table-hover">
                <thead>
                  <tr>
                    <th>Link Type</th>
                    <th>Scope</th>
                    <th>Created By</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sharingLinks.map((link) => (
                    <tr key={link.id}>
                      <td>
                        <span className="badge bg-info text-white">
                          {link.linkType}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {link.scope}
                        </span>
                      </td>
                      <td className="small">{link.createdBy ?? "—"}</td>
                      <td className="small">
                        {link.createdDateTime
                          ? formatDate(link.createdDateTime)
                          : "—"}
                      </td>
                      <td className="small">
                        {link.expirationDateTime
                          ? formatDate(link.expirationDateTime)
                          : "Never"}
                      </td>
                      <td className="text-end">
                        <Button
                          as="a"
                          href={link.webUrl}
                          target="_blank"
                          rel="noreferrer"
                          variant="outline-primary"
                          size="sm"
                        >
                          <FaExternalLinkAlt /> Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {isSuperUser && tab === "archive" && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="text-muted small">
                Start and monitor group archive runs.
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleStartArchive}
                disabled={archiveActionLoading}
              >
                {archiveActionLoading ? "Queueing..." : "Start Archive Backup"}
              </Button>
            </div>

            {archiveLoading && <div className="text-muted small">Loading archive runs...</div>}

            {!archiveLoading && archiveRuns.length === 0 && (
              <div className="text-muted small">No archive runs found for this group.</div>
            )}

            {!archiveLoading && archiveRuns.length > 0 && (
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>Stage</th>
                      <th>Status</th>
                      <th>Requested By</th>
                      <th>Requested</th>
                      <th>Request Guid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archiveRuns.map((run) => (
                      <tr key={run.guid}>
                        <td className="text-capitalize">{run.stageName}</td>
                        <td>
                          <span className={`badge ${
                            run.reportStatus === "Complete"
                              ? "bg-success"
                              : run.reportStatus === "Failed"
                              ? "bg-danger"
                              : "bg-info"
                          }`}>
                            {run.reportStatus}
                          </span>
                        </td>
                        <td>{run.createUser}</td>
                        <td>{formatDate(run.createDt)}</td>
                        <td className="small font-monospace">{run.guid}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Kept mounted after first open so switching tabs doesn't discard a loaded inventory. */}
        {canViewFileInventory && fileInventoryGroupId && (tab === "files" || fileInventoryOpened) && (
          <div className={tab === "files" ? "" : "d-none"}>
            <ResourceFileInventory key={fileInventoryGroupId} groupId={fileInventoryGroupId} />
          </div>
        )}

        {/* Permissions Tab */}
        {isSuperUser && tab === "permissions" && (
          <>
            <div className="alert alert-info small mb-3">
              <strong>Permission Management</strong>
              <p className="mb-0 mt-2">
                Use these tools to view and troubleshoot SharePoint permissions.
              </p>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <h6 className="card-title">
                      <FaUsers className="me-2" />
                      View All Site Permissions
                    </h6>
                    <p className="card-text small text-muted">
                      See everyone who has access to this site, their permission levels,
                      and how they received access.
                    </p>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => setShowSitePermissionsModal(true)}
                    >
                      View Site Permissions
                    </Button>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="card h-100">
                  <div className="card-body">
                    <h6 className="card-title">
                      <FaUserPlus className="me-2" />
                      Check User Permissions
                    </h6>
                    <p className="card-text small text-muted">
                      Check if a specific user has access and view their permission level
                      on this site or specific items.
                    </p>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => setShowCheckPermissionsModal(true)}
                    >
                      Check Permissions
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-3 p-3 border rounded bg-light small">
              <strong>Permission Inheritance:</strong>
              <ul className="mb-0 mt-2 ps-3">
                <li>Site-level permissions apply to all lists and libraries by default</li>
                <li>Items with broken inheritance have unique permissions (shown with 🔒)</li>
                <li>Users can have access through: Direct permissions, Group membership, or Sharing links</li>
              </ul>
            </div>
          </>
        )}
      </div>

      {/* Add User Modal */}
      <AddUserModal
        show={showAddUserModal}
        onHide={() => setShowAddUserModal(false)}
        onAddUser={handleAddUser}
        groupId={details?.groupId ?? null}
      />

      {/* Site Saved Data Modal */}
      <SiteSavedDataModal
        show={showSiteDataModal}
        onHide={() => setShowSiteDataModal(false)}
        onSave={handleSaveSiteData}
        initialData={metadataInitialData}
        groupId={details?.groupId ?? null}
      />

      {/* Delete Group Modal */}
      {currentUserIsOwner && (
        <DeleteGroupModal
          show={showDeleteModal}
          groupName={details?.displayName || ''}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteGroup}
        />
      )}

      {/* Support Request Modal */}
      <SupportRequestModal
        show={showSupportModal}
        groupName={details?.displayName || ''}
        groupId={internalResourceId || ''}
        groupUrl={details?.url || details?.siteUrl || details?.webUrl}
        userDisplayName={accounts[0]?.name}
        userEmail={accounts[0]?.username}
        onClose={() => setShowSupportModal(false)}
        onSubmit={handleSupportRequest}
      />

      {/* Site Permissions Modal */}
      <SitePermissionsModal
        show={showSitePermissionsModal}
        groupName={details?.displayName || ''}
        internalResourceId={internalResourceId || ''}
        onClose={() => setShowSitePermissionsModal(false)}
        onUserClick={(userId) => {
          const user = members.find(m => m.id === userId);
          if (user) {
            setSelectedUserForDetails({ id: userId, name: user.displayName });
            setShowSitePermissionsModal(false);
          }
        }}
      />

      {/* Check Permissions Modal */}
      <CheckPermissionsModal
        show={showCheckPermissionsModal}
        groupName={details?.displayName || ''}
        internalResourceId={internalResourceId || ''}
        onClose={() => setShowCheckPermissionsModal(false)}
      />

      {/* User Access Details Modal */}
      {selectedUserForDetails && (
        <UserAccessDetailsModal
          show={!!selectedUserForDetails}
          userId={selectedUserForDetails.id}
          userName={selectedUserForDetails.name}
          internalResourceId={internalResourceId || ''}
          groupName={details?.displayName || ''}
          onClose={() => setSelectedUserForDetails(null)}
        />
      )}

      {/* Permissions Modal */}
      {permissionsModalItem && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Item Permissions</h5>
                <button type="button" className="btn-close" onClick={handleClosePermissionsModal}></button>
              </div>
              <div className="modal-body">
                {(() => {
                  const fields = permissionsModalItem.fields?.additionalData || permissionsModalItem.fields || {};
                  const itemName = fields.FileLeafRef || fields.Title || fields.Name || 'Untitled';
                  const modified = fields.Modified || permissionsModalItem.lastModifiedDateTime;
                  const modifiedBy = fields.Editor || fields.Author;
                  
                  return (
                    <>
                      <div className="mb-3">
                        <strong>Item:</strong> {itemName}
                      </div>
                      <div className="alert alert-info">
                        <p className="mb-0">
                          <strong>Note:</strong> To check detailed permissions, please open this item in SharePoint.
                        </p>
                        {permissionsModalItem.webUrl && (
                          <Button
                            as="a"
                            href={permissionsModalItem.webUrl}
                            target="_blank"
                            rel="noreferrer"
                            variant="primary"
                            size="sm"
                            className="mt-2"
                          >
                            <FaExternalLinkAlt className="me-1" />
                            Open in SharePoint
                          </Button>
                        )}
                      </div>
                      <div className="small text-muted">
                        <p><strong>Item ID:</strong> {permissionsModalItem.id}</p>
                        {modified && (
                          <p><strong>Modified:</strong> {new Date(modified).toLocaleString()}</p>
                        )}
                        {modifiedBy && (
                          <p><strong>Modified By:</strong> {modifiedBy}</p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <Button variant="secondary" onClick={handleClosePermissionsModal}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      <Modal show={successModal.show} onHide={() => setSuccessModal({ show: false, title: '', message: '' })} centered>
        <Modal.Header closeButton>
          <Modal.Title>{successModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{successModal.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setSuccessModal({ show: false, title: '', message: '' })}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Modal */}
      <Modal show={errorModal.show} onHide={() => setErrorModal({ show: false, message: '' })} centered>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorModal.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setErrorModal({ show: false, message: '' })}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Modal */}
      <Modal show={confirmModal.show} onHide={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: async () => {} })} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{confirmModal.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: async () => {} })}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmModal.onConfirm}>
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ResourceDetailsPanel;